/**
 * Copy text to the clipboard, with a fallback for Telegram's in-app WebView.
 *
 * `navigator.clipboard` is missing or blocked in several Telegram clients, so a
 * bare `writeText` quietly fails exactly where students need it most — the
 * webview cannot download a file, and copying the link out to a real browser is
 * the way around that. The execCommand path is deprecated but still works in
 * the clients where the modern API does not.
 *
 * Resolves true when the text is on the clipboard, false when it isn't, so the
 * caller can show the link instead of pretending the copy worked.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* blocked or insecure context — try the old way */ }

  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    // Keep it off-screen but still focusable: display:none would not be selectable.
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:-9999px;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);   // iOS needs the explicit range
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
